import React, { type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Badge from '@atlaskit/badge';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { RestrictedFieldGroupComponentProps } from '../../../types.tsx';
import { isGlobalAdmin } from '../../../utils/is-global-admin/index.tsx';
import { IPHLink } from '../../iph-link/index.tsx';
import { ExpandableSection } from '../expandable-section/index.tsx';
import { FieldGroup } from '../field-group/index.tsx';
import { SidebarDividingLine } from '../styled.tsx';

const PRODUCT_HELP_ARTICLE_ID = '6I42ggwGORmP84atFaD3hS';
const PRODUCT_HELP_LINK =
	'https://support.atlassian.com/jira-cloud-administration/docs/configure-a-custom-field/#Troubleshoot-issue-configuration-and-context';

export const RestrictedFieldGroup = ({
	restrictedFieldGroupMessages,
	...props
}: RestrictedFieldGroupComponentProps) => {
	const { formatMessage } = useIntl();

	const restrictedFieldSectionLabel = (
		<RestrictedFieldSectionLabelContainer>
			{fg('mcrtb-1008-enghealth-replace-typog-css-with-tokens') ? (
				<Text as="span">
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? restrictedFieldGroupMessages.labelIssueTermRefresh
							: restrictedFieldGroupMessages.label,
					)}
				</Text>
			) : (
				/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */
				<span>
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? restrictedFieldGroupMessages.labelIssueTermRefresh
							: restrictedFieldGroupMessages.label,
					)}
				</span>
			)}
			<Badge>{props.totalCount}</Badge>
		</RestrictedFieldSectionLabelContainer>
	);

	const restrictedFieldDescription = () => {
		const descriptionGlobalAdminMessage = fg('jira-issue-terminology-refresh-m3')
			? restrictedFieldGroupMessages.descriptionGlobalAdminIssueTermRefresh
			: restrictedFieldGroupMessages.descriptionGlobalAdmin;

		const descriptionNonGlobalAdminMessage = fg('jira-issue-terminology-refresh-m3')
			? restrictedFieldGroupMessages.descriptionNonGlobalAdminIssueTermRefresh
			: restrictedFieldGroupMessages.descriptionNonGlobalAdmin;

		return isGlobalAdmin(props.tenant)
			? descriptionGlobalAdminMessage
			: descriptionNonGlobalAdminMessage;
	};

	// @ts-expect-error - TS2769 - No overload matches this call.
	const restrictedFieldSectionDescription = formatMessage(restrictedFieldDescription(), {
		link: (text: string[]): ReactNode => (
			<IPHLink
				articleId={PRODUCT_HELP_ARTICLE_ID}
				documentationUrl={PRODUCT_HELP_LINK}
				linkText={{
					// Text that we get is already translated at this point and we're interested in the pure string only, so we only add this random id to comply with the API
					// and to make sure no real translation will be put under this fake key
					id: 'c29tZSBoYXNoIGZvciByZWFjdC1pbnRsIGtleQ==.no-translate',
					defaultMessage: text[0],
				}}
			/>
		),
	});

	return (
		<Box xcss={restrictedFieldGroupContainerStyles}>
			<ExpandableSection
				label={restrictedFieldSectionLabel}
				description={restrictedFieldSectionDescription}
			>
				<FieldGroup {...props} />
				<DividingLineContainer>
					<SidebarDividingLine />
				</DividingLineContainer>
			</ExpandableSection>
		</Box>
	);
};

const restrictedFieldGroupContainerStyles = xcss({
	marginTop: 'space.050',
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RestrictedFieldSectionLabelContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> *:first-child': {
		marginRight: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DividingLineContainer = styled2.div({
	// TODO: Functional components shouldn't own spacing. Spacing is layout components responsibility.
	// Make Sidebar own padding and remove paddings from functional components inside the sidebar, then remove negative margins hack;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `${token('space.300')} -${gridSize * 2}px 0`,
});
